import { useEffect, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ChartDataSets } from 'chart.js';
import { format } from 'date-fns';
import axios, { CanceledError } from 'axios';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Bounds, Data, DatasetsSupplier } from './types';

type FetchOptions = {
  includeFic?: boolean;
  quarterly?: boolean;
};

async function fetchData<T>(
  url: string,
  { min, max }: Bounds,
  options?: { [key: string]: boolean | string | undefined },
  signal?: AbortSignal
) {
  const startDate = min ? format(min, 'yyyy-MM-dd') : undefined;
  const endDate = max ? format(max, 'yyyy-MM-dd') : undefined;
  const { data } = await axios.get<T>(url, {
    params: { startDate, endDate, ...options },
    signal,
  });

  return data;
}

function createUseFetchHook(fetchDatasets: DatasetsSupplier) {
  return ({ min, max }: Bounds, partitionKeys?: string) => {
    const [loading, setLoading] = useState(false);
    const [datasets, setDatasets] = useState<ChartDataSets[]>([]);

    useEffect(() => {
      const controller = new AbortController();
      setLoading(true);
      fetchDatasets({ min, max }, partitionKeys, controller.signal)
        .then((chartDatasets) => {
          setLoading(false);
          setDatasets(chartDatasets.map((dataset) => ({ ...dataset, categoryPercentage: 0.6 })));
        })
        .catch((e) => {
          if (!(e instanceof CanceledError)) throw e;
        });

      return () => controller.abort();
      // eslint-disable-next-line
    }, [min, max, partitionKeys, fetchDatasets]);

    return [datasets, loading] as const;
  };
}

const fetchDynamicDatasets = (options?: FetchOptions) => (
  bounds: Bounds,
  partitionKeys?: string,
  signal?: AbortSignal
) =>
  fetchData<ChartDataSets[]>(
    'https://func-onelife.azurewebsites.net/api/chartdatasets/dynamic?code=84A9nh3yldi2qssYyUXdNaVsLIenMGNqnErtaWjh4rflLpwftFNCiA==',
    bounds,
    { ...options, partitionKeys },
    signal
  );

const fetchDynamicValuations = (options?: FetchOptions) => (bounds: Bounds) =>
  fetchData<Data[]>(
    'https://func-onelife.azurewebsites.net/api/valuations/dynamic?code=OwHSBDwCN28JBogHSXih3ZF23tGObhbsvHORXwoGKKcOcl3NgGex/A==',
    bounds,
    options
  );

export const useFetchDynamicDataSets = (options?: FetchOptions) => createUseFetchHook(fetchDynamicDatasets(options));

const fetchFundamentalDatasets = (options?: FetchOptions) => (
  bounds: Bounds,
  partitionKeys?: string,
  signal?: AbortSignal
) =>
  fetchData<ChartDataSets[]>(
    'https://func-onelife.azurewebsites.net/api/chartdatasets/fundamental?code=3Ykl74dq5V5Xxg6CHhnrHSq0zSFIMfTjm0pWqcvaQwDymaodq6elug==',
    bounds,
    { ...options, partitionKeys },
    signal
  );

const fetchFundamentalValuations = (options?: FetchOptions) => (bounds: Bounds) =>
  fetchData<Data[]>(
    'https://func-onelife.azurewebsites.net/api/valuations/fundamental?code=moxvRFsayaEclUzE4dxR/KgdAQT/PR81aTRNzD0ZXlHna6AyGSWaLA==',
    bounds,
    options
  );

export const useFetchFundamentalDataSets = (options?: FetchOptions) =>
  createUseFetchHook(fetchFundamentalDatasets(options));

const fetchConvictionDatasets = (options?: FetchOptions) => (
  bounds: Bounds,
  partitionKeys?: string,
  signal?: AbortSignal
) =>
  fetchData<ChartDataSets[]>(
    'https://func-onelife.azurewebsites.net/api/chartdatasets/conviction?code=mYmObUsVluIMKnXWtLmHzoZ5uHqXfO27M-Ts_iC3RdJ5AzFu29JmTw%3D%3D',
    bounds,
    { ...options, partitionKeys },
    signal
  );

// eslint-disable-next-line
const fetchConvictionValuations = (options?: FetchOptions) => (bounds: Bounds) =>
  fetchData<Data[]>(
    'https://func-onelife.azurewebsites.net/api/valuations/conviction?code=RIQkNKNRJYaactziVXlp3N4Qtc344nKbyt7Ef%2FPEa6ebDOqnSePhXg%3D%3D',
    bounds,
    options
  );

export const useFetchConvictionDataSets = (options?: FetchOptions) =>
  createUseFetchHook(fetchConvictionDatasets(options));

const fetchFundsmithValuations = (options?: FetchOptions) => (bounds: Bounds) =>
  fetchData<Data[]>(
    'https://func-onelife.azurewebsites.net/api/valuations/fundsmith?code=RIQkNKNRJYaactziVXlp3N4Qtc344nKbyt7Ef/PEa6ebDOqnSePhXg==',
    bounds,
    options
  );

const fetchAphilionValuations = (options?: FetchOptions) => (bounds: Bounds) =>
  fetchData<Data[]>(
    'https://func-onelife.azurewebsites.net/api/valuations/aphilion?code=RIQkNKNRJYaactziVXlp3N4Qtc344nKbyt7Ef/PEa6ebDOqnSePhXg==',
    bounds,
    options
  );

export function fetchWeightedDataSets(
  dynamicWeight: number,
  fundamentalWeight: number,
  convictionWeight: number,
  additional: 'fundsmith' | 'aphilion',
  includeAdditional: boolean
) {
  return ({ min, max }: Bounds) => {
    const [loading, setLoading] = useState(false);
    const [dynamicDataSet, setDynamicDataSet] = useState<ChartDataSets>({
      label: 'Connect Dynamic',
      borderColor: '#222A35',
      backgroundColor: '#222A35',
      fill: false,
      data: [],
    });
    const [fundamentalDataSet, setFundamentalDataSet] = useState<ChartDataSets>({
      label: 'Connect Fundamental',
      borderColor: '#6D6E71',
      backgroundColor: '#6D6E71',
      fill: false,
      data: [],
    });
    const [convictionDataSet, setConvictionDataSet] = useState<ChartDataSets>({
      label: 'Connect Conviction',
      borderColor: '#7393B3',
      backgroundColor: '#7393B3',
      fill: false,
      data: [],
    });
    const [additionalDataSet, setAdditionalDataSet] = useState<ChartDataSets>({
      borderColor: '#D0D0D2',
      backgroundColor: '#D0D0D2',
      fill: false,
      data: [],
    });
    const [weightedDataSet, setWeightedDataSet] = useState<ChartDataSets>({
      label: 'Gewogen portefeuille',
      borderColor: '#B28E5C',
      backgroundColor: '#B28E5C',
      fill: false,
      data: [],
    });

    useEffect(() => {
      (async () => {
        setLoading(true);
        const dynamicValuations = await fetchDynamicValuations()({ min, max });
        setDynamicDataSet({ ...dynamicDataSet, data: dynamicValuations });
        const fundamentalValuations = await fetchFundamentalValuations()({ min, max });
        setFundamentalDataSet({ ...fundamentalDataSet, data: fundamentalValuations });
        const convictionValuations = await fetchConvictionValuations()({ min, max });
        setConvictionDataSet({ ...convictionDataSet, data: convictionValuations });
        const additionalValuations =
          additional === 'fundsmith'
            ? await fetchFundsmithValuations()({ min, max })
            : await fetchAphilionValuations()({ min, max });
        setAdditionalDataSet({
          ...additionalDataSet,
          label: additional === 'fundsmith' ? 'Fundsmith Equity' : 'Aphilion Q² SICAV',
          data: additionalValuations.filter((valuation) => dynamicValuations.some(({ t }) => valuation.t === t)),
        });
        setLoading(false);
      })();
      // eslint-disable-next-line
    }, [additional, min, max]);

    useEffect(() => {
      const dynamicValuations = dynamicDataSet.data as Data[];
      const fundamentalValuations = fundamentalDataSet.data as Data[];
      const convictionValuations = convictionDataSet.data as Data[];
      const additionalValuations = additionalDataSet.data as Data[];

      const weightedValuations: Data[] = [];

      const sharedDates = dynamicValuations
        .filter((valuation) => convictionWeight === 0 || convictionValuations.some(({ t }) => valuation.t === t))
        .map(({ t }) => t);

      // eslint-disable-next-line no-restricted-syntax
      for (const date of sharedDates) {
        const dynamicValuation = dynamicValuations.find(({ t }) => t === date);
        const convictionValuation = convictionValuations.find(({ t }) => t === date);
        const fundamentalValuation = fundamentalValuations.find(({ t }) => t === date);
        const additionalValuation = additionalValuations.find(({ t }) => t === date);

        if (
          !dynamicValuation ||
          (convictionWeight > 0 && !convictionValuation) ||
          !fundamentalValuation ||
          !additionalValuation
        )
          break;

        const weightedValuation = {
          t: dynamicValuation.t,
          y: +(
            (dynamicValuation.y * dynamicWeight +
              (convictionValuation?.y ?? 0) * convictionWeight +
              fundamentalValuation.y * fundamentalWeight) /
            100.0
          ).toFixed(2),
        };

        if (includeAdditional)
          weightedValuation.y = +(
            weightedValuation.y +
            (additionalValuation.y * (100 - dynamicWeight - convictionWeight - fundamentalWeight)) / 100.0
          ).toFixed(2);

        weightedValuations.push(weightedValuation);
      }

      setWeightedDataSet({ ...weightedDataSet, data: weightedValuations });
      // eslint-disable-next-line
    }, [
      dynamicWeight,
      fundamentalWeight,
      convictionWeight,
      additional,
      includeAdditional,
      dynamicDataSet,
      fundamentalDataSet,
      convictionDataSet,
      additionalDataSet,
    ]);

    const datasets: ChartDataSets[] = [weightedDataSet, dynamicDataSet, fundamentalDataSet, convictionDataSet];
    if (includeAdditional) datasets.push(additionalDataSet);

    return [datasets, loading] as const;
  };
}
